import * as React from 'react';

import {
  IHairTrackingEvent,
  useBloomreachTracking,
  useDatabaseTracking,
} from '@notino/hair-analyzer-lib';

import { useApolloClientHeaders } from '@hooks/useApolloClientHeaders';
import { useUser } from '@queries/useUser';

import { HairAnalyzerConfig } from '../../model';
import { apiClientFactory } from '../api';

import { useTracking } from './useTracking';

interface UseCombinedTrackingArgs {
  brand: HairAnalyzerConfig;
}

/**
 * Fans a single Revieve tracking event out to all three consumers, the same way
 * the standalone hair-analyzer-fragment does:
 *  - GTM (our own product/promotion events)
 *  - hair-analyzer-api (advisor data, via the lib's useDatabaseTracking)
 *  - Bloomreach (questionnaire funnel, via the lib's useBloomreachTracking)
 */
export const useCombinedTracking = ({ brand }: UseCombinedTrackingArgs) => {
  const {
    hairAnalyzer: { apiUrl },
  } = useApolloClientHeaders();
  const { isLoggedIn } = useUser();

  const clientApi = React.useMemo(() => apiClientFactory({ apiUrl }), [apiUrl]);

  const { dispatchTrackingEvent: dispatchGtmEvent } = useTracking();
  const { onTrackingEvent: saveToDatabase } = useDatabaseTracking({
    clientApi,
    isUserRegistered: isLoggedIn,
  });
  // On product detail the tool is always brand specific, so passing the
  // product's brand makes the lib report the session as 'Monobrand'.
  const { dispatchBloomreachTrackingEvent } = useBloomreachTracking({
    brandNames: brand.brands ?? [],
    queryBrandName: brand.brandName,
  });

  return React.useCallback(
    async (event: IHairTrackingEvent | undefined) => {
      if (!event) {
        return;
      }

      await Promise.allSettled([
        dispatchGtmEvent(event),
        saveToDatabase(event),
        dispatchBloomreachTrackingEvent(event),
      ]);
    },
    [dispatchGtmEvent, saveToDatabase, dispatchBloomreachTrackingEvent]
  );
};
